import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link2, Lock, Loader2, CheckCircle, ShieldCheck, RefreshCw, Unlink, AlertTriangle, FlaskConical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const PublicConnect = ({ onPositionAction }) => {
  const [status, setStatus] = useState({ connected: false, is_mock: true, loading: true });
  const [secret, setSecret] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [loadingPortfolio, setLoadingPortfolio] = useState(false);
  const [portfolio, setPortfolio] = useState(null);
  const [needsLogin, setNeedsLogin] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/brokerage/public/status`, { withCredentials: true });
      setStatus({ connected: data.connected, is_mock: data.is_mock, loading: false });
      setNeedsLogin(false);
    } catch (err) {
      if (err.response?.status === 401) setNeedsLogin(true);
      setStatus((s) => ({ ...s, loading: false }));
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const handleConnect = async () => {
    if (!secret.trim()) {
      toast.error('Enter your Public secret key to connect.');
      return;
    }
    setConnecting(true);
    try {
      const { data } = await axios.post(`${API}/brokerage/public/connect`, { secret: secret.trim() }, { withCredentials: true });
      setSecret(''); // clear from memory immediately
      toast.success(data.is_mock ? 'Connected in MOCK mode (sample data).' : `Connected! ${data.accounts?.length || 0} account(s).`);
      await loadStatus();
      await loadPortfolio();
    } catch (err) {
      if (err.response?.status === 401) {
        setNeedsLogin(true);
        toast.error('Please sign in to Option Wizard first, then connect Public.');
      } else {
        toast.error(err.response?.data?.detail || 'Could not connect to Public.');
      }
    } finally {
      setConnecting(false);
    }
  };

  const loadPortfolio = async () => {
    setLoadingPortfolio(true);
    try {
      const { data } = await axios.post(`${API}/brokerage/public/portfolio`, {}, { withCredentials: true });
      if (data.success) {
        setPortfolio(data);
      } else {
        toast.error(data.error || 'Unable to load Public portfolio.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to load Public portfolio.');
    } finally {
      setLoadingPortfolio(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.post(`${API}/brokerage/public/disconnect`, {}, { withCredentials: true });
      setPortfolio(null);
      toast.success('Public account disconnected.');
      await loadStatus();
    } catch (err) {
      toast.error('Could not disconnect.');
    }
  };

  return (
    <Card className="glass-effect border-0 shadow-lg mt-4" data-testid="public-connect-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center">
            <Link2 className="h-5 w-5 mr-2 text-teal-600" />
            Public.com Connection
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-teal-700 border-teal-200 bg-teal-50">Read-only</Badge>
            {status.is_mock && (
              <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50" data-testid="public-mock-badge">
                <FlaskConical className="h-3 w-3 mr-1" /> MOCK
              </Badge>
            )}
          </div>
        </CardTitle>
        <CardDescription>
          Connect your Public account with your personal API secret key to sync positions (read-only, no trading).
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {status.is_mock && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="public-mock-banner">
            <FlaskConical className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Mock mode:</strong> The live Public connection isn&apos;t enabled yet, so any data shown here is
              <strong> sample/mock data</strong>, not real Public account data. The adapter is production-ready and will use
              real data once a Public secret key is configured.
            </span>
          </div>
        )}

        {needsLogin ? (
          <div className="flex flex-col items-center justify-center py-6 space-y-3 text-center">
            <div className="p-3 bg-teal-100 rounded-full"><Lock className="h-8 w-8 text-teal-600" /></div>
            <p className="text-sm text-gray-600 max-w-sm">Sign in to Option Wizard (top-right &quot;Sign In&quot;) to connect a Public account. Your key is stored encrypted server-side and bound to your account.</p>
          </div>
        ) : !status.connected ? (
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">Public personal Secret key</label>
            <Input
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={status.is_mock ? 'Any value works in MOCK mode' : 'Paste your Public secret key'}
              autoComplete="new-password"
              spellCheck={false}
              data-testid="public-secret-input"
            />
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <ShieldCheck className="h-4 w-4 text-green-600" />
              <span>Stored encrypted server-side. Never shown to the browser, logged, or sent to AI.</span>
            </div>
            <Button
              onClick={handleConnect}
              disabled={connecting}
              className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
              data-testid="public-connect-btn"
            >
              {connecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Connecting...</> : <>Connect read-only account</>}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Connected to Public</span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={loadPortfolio} disabled={loadingPortfolio} data-testid="public-refresh-btn">
                  {loadingPortfolio ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  <span className="ml-1">Refresh</span>
                </Button>
                <Button variant="outline" size="sm" onClick={handleDisconnect} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="public-disconnect-btn">
                  <Unlink className="h-4 w-4 mr-1" /> Disconnect
                </Button>
              </div>
            </div>

            {portfolio && (
              <div className="space-y-3" data-testid="public-portfolio-summary">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat label="Total Value" value={money(portfolio.balances?.net_liquidating_value)} />
                  <Stat label="Cash" value={money(portfolio.balances?.cash_balance)} />
                  <Stat label="Options BP" value={money(portfolio.balances?.derivative_buying_power)} />
                  <Stat label="Positions" value={`${(portfolio.equity_positions?.length || 0) + (portfolio.options_positions?.length || 0) + (portfolio.spreads?.length || 0)}`} />
                </div>

                <div className="border rounded-lg divide-y">
                  {(portfolio.spreads || []).map((s, i) => (
                    <Row key={`sp-${i}`} left={`${s.underlying_symbol} · ${s.strategy_name}`} right={money(s.total_market_value)} badge="Spread" onClick={() => onPositionAction && onPositionAction(s, 'analyze')} />
                  ))}
                  {(portfolio.options_positions || []).map((p, i) => (
                    <Row key={`op-${i}`} left={`${p.underlying_symbol} $${p.strike_price} ${(p.option_type || '').toUpperCase()} ×${p.quantity}`} right={money(p.market_value)} badge="Option" onClick={() => onPositionAction && onPositionAction(p, 'analyze')} />
                  ))}
                  {(portfolio.equity_positions || []).map((p, i) => (
                    <Row key={`eq-${i}`} left={`${p.underlying_symbol} ×${p.quantity}`} right={money(p.market_value)} badge="Stock" />
                  ))}
                </div>
              </div>
            )}

            {!portfolio && (
              <Button onClick={loadPortfolio} disabled={loadingPortfolio} variant="outline" data-testid="public-load-portfolio-btn">
                {loadingPortfolio ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading...</> : 'Load Portfolio'}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value }) => (
  <div className="p-3 bg-gray-50 rounded-lg">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-base font-semibold text-gray-800">{value}</div>
  </div>
);

const Row = ({ left, right, badge, onClick }) => (
  <div className={`flex items-center justify-between p-3 ${onClick ? 'cursor-pointer hover:bg-teal-50' : ''}`} onClick={onClick}>
    <div className="flex items-center gap-2">
      <Badge variant="outline" className="text-xs">{badge}</Badge>
      <span className="text-sm font-medium text-gray-800">{left}</span>
    </div>
    <span className="text-sm text-gray-700">{right}</span>
  </div>
);

export default PublicConnect;
